import type { CSSProperties } from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import {
  LA_FALLBACK,
  VIEWPORT_DEBOUNCE_MS
} from "../redux/global-map.constants"
import type {
  EntityType,
  FallbackCenter,
  MapCluster,
  MapFeature,
  MapFeaturesResponse,
  MapViewport
} from "../redux/global-map.types"
import MarkerHoverTooltip from "./MarkerHoverTooltip"
import type { FitBounds } from "./fit"
import { loadGoogleMaps, onMapsAuthFailure } from "./loadGoogleMaps"
import { renderOverlays } from "./markers"
import "./markers.less"

// Google's own heuristic can zoom a tight cluster of bounds in arbitrarily
// far (a same-address duplicate would fit at max zoom) — Spec §5 caps it.
const MAX_FIT_ZOOM = 15
const FIT_BOUNDS_PADDING_PX = 48

// `markers.ts`'s `renderOverlays` constructs every marker as a
// `google.maps.marker.AdvancedMarkerElement`, which Google silently refuses
// to attach to a map with no Map ID — not a styling downgrade, zero markers
// render. Google publishes "DEMO_MAP_ID" as a placeholder that works with any
// key, no Cloud Console setup required, specifically for this case; it only
// gives up custom vector styling, which this app doesn't use.
const DEMO_MAP_ID = "DEMO_MAP_ID"

// "Look Around a Zip Code" — the center pin + radius circle's shared color
// (`markers.less`'s `@global-map-near-center-color`, kept in sync by hand;
// a `google.maps.Circle`'s `fillColor`/`strokeColor` take a raw CSS color
// string, not a class, so this can't just reference the LESS variable).
const NEAR_CENTER_COLOR = "#0f172a"
const METERS_PER_MILE = 1609.34

interface MapCanvasProps {
  // The plan's own task-17 brief predates Task 18's real `renderOverlays`
  // signature and described this as separate `mode`/`features`/`clusters`
  // props. `renderOverlays` actually takes the whole response object, and
  // reconstructing one from three loose props here would mean fabricating
  // the response fields this component never has (query_fingerprint, total,
  // ...) just to satisfy the type. Taking the response as-is is what the
  // eventual caller already holds (`GlobalMapState.data`), so it is passed
  // straight through instead.
  data: MapFeaturesResponse | null
  fitTo: FallbackCenter | FitBounds | null
  selectedId: string | null
  reducedMotion: boolean
  // "Look Around a Zip Code" — the resolved zip centroid and the committed
  // radius, drawn as a small center pin plus a translucent circle overlay
  // (Spec: "show a center of zip code and the radius area"). Both null
  // together whenever the filter isn't active; `MapTab.tsx` is the only
  // caller and always passes them as a pair (mirroring `nearZip`/
  // `nearRadiusMi` themselves, which the backend also requires together).
  nearCenter: { latitude: number; longitude: number } | null
  nearRadiusMi: number | null
  onSelectFeature: (id: string, entityType: EntityType) => void
  onClusterClick: (cluster: MapCluster) => void
  onViewportChange: (v: MapViewport) => void
  onError: (code: string) => void
  onLocateMe: () => void
  locateMeLabel: string
  fullscreenLabel: string
  changeMapStyleLabel: string
  mapTypeLabel: string
  satelliteTypeLabel: string
}

function isFallbackCenter(
  fitTo: FallbackCenter | FitBounds
): fitTo is FallbackCenter {
  return "latitude" in fitTo
}

const TOOLTIP_MARGIN_PX = 8
// `MarkerHoverTooltip`'s own `max-w-[220px]` — used only to clamp this
// component's placement away from the container's left/right edges; the
// tooltip's real rendered width can be narrower.
const TOOLTIP_MAX_WIDTH_PX = 220
// An estimate of the tooltip's own rendered height (glyph + up to 3 short
// lines inside `px-3 py-2` padding — measured against the real component in
// a live browser at ~64px, rounded up for margin of error) — used only to
// decide whether there's enough headroom to place the tooltip ABOVE the
// marker at all. Like `TOOLTIP_MAX_WIDTH_PX`, this is an estimate, not a
// measured value; the real-browser check is what actually confirms it never
// spills outside the container.
const TOOLTIP_ESTIMATED_HEIGHT_PX = 80

// Positions the tooltip near the hovered marker. `rect` is the marker's own
// `content` element's live `getBoundingClientRect()` — already real screen
// (viewport) coordinates, captured by `markers.ts` at the moment the mouse
// entered it, so no Maps `OverlayView`/projection API is needed here at
// all. Translated into this component's own container-relative frame (the
// tooltip is an absolutely-positioned sibling of the map's own container
// div), then clamped on BOTH axes so it doesn't render off any edge of that
// container near the map's bounds: horizontally by shifting left/right
// within `[minX, maxX]` (the tooltip stays centered on the marker, just
// bounded), and vertically by flipping to render BELOW the marker instead
// of above it whenever there isn't enough headroom for the estimated
// tooltip height — clamping the `top` value alone (the way the horizontal
// axis clamps `left`) would still let the tooltip's own height, which
// `translate(-50%, -100%)` pushes upward from that anchor, spill above the
// container, since nothing here clips it. jsdom cannot see real layout, so
// the exact pixel result here still needs the real-browser verification the
// brief calls for.
function tooltipPositionStyle(
  rect: DOMRect,
  container: HTMLElement | null
): CSSProperties {
  if (!container) return { display: "none" }

  const containerRect = container.getBoundingClientRect()
  const markerCenterX = rect.left - containerRect.left + rect.width / 2
  const markerTop = rect.top - containerRect.top
  const markerBottom = rect.bottom - containerRect.top

  const halfTooltip = TOOLTIP_MAX_WIDTH_PX / 2
  const minX = halfTooltip + TOOLTIP_MARGIN_PX
  const maxX = Math.max(
    containerRect.width - halfTooltip - TOOLTIP_MARGIN_PX,
    minX
  )
  const clampedX = Math.min(Math.max(markerCenterX, minX), maxX)

  const fitsAbove =
    markerTop - TOOLTIP_ESTIMATED_HEIGHT_PX - TOOLTIP_MARGIN_PX >= 0

  return fitsAbove
    ? {
        position: "absolute",
        left: clampedX,
        top: markerTop - TOOLTIP_MARGIN_PX,
        transform: "translate(-50%, -100%)"
      }
    : {
        position: "absolute",
        left: clampedX,
        top: markerBottom + TOOLTIP_MARGIN_PX,
        transform: "translate(-50%, 0)"
      }
}

function readViewport(map: google.maps.Map): MapViewport | null {
  const bounds = map.getBounds()
  const zoom = map.getZoom()
  if (!bounds || zoom === undefined) return null

  const ne = bounds.getNorthEast()
  const sw = bounds.getSouthWest()
  return {
    north: ne.lat(),
    south: sw.lat(),
    east: ne.lng(),
    west: sw.lng(),
    zoom
  }
}

// `setCenter`/`setZoom` may still play Google's own default zoom transition;
// `moveCamera` is documented to apply "without animation" — the one part of
// this contract `reducedMotion` can actually guarantee. There is no
// bounds-based equivalent of `moveCamera` (it takes a center/zoom, not a
// box), so the bounds branch always goes through `fitBounds` regardless of
// `reducedMotion` — `fitBounds` itself only animates "depending on an
// internal heuristic" and never a guaranteed pan the way `panTo` is.
function applyFit(
  map: google.maps.Map,
  fitTo: FallbackCenter | FitBounds,
  reducedMotion: boolean
): void {
  if (isFallbackCenter(fitTo)) {
    const center = { lat: fitTo.latitude, lng: fitTo.longitude }
    if (reducedMotion) {
      map.moveCamera({ center, zoom: fitTo.zoom })
    } else {
      map.setCenter(center)
      map.setZoom(fitTo.zoom)
    }
    return
  }

  if (!fitTo.bounds.length) return

  const lats = fitTo.bounds.map((point) => point.latitude)
  const lngs = fitTo.bounds.map((point) => point.longitude)
  map.fitBounds(
    {
      north: Math.max(...lats),
      south: Math.min(...lats),
      east: Math.max(...lngs),
      west: Math.min(...lngs)
    },
    FIT_BOUNDS_PADDING_PX
  )

  // `fitBounds` has no direct "max zoom" option — the standard workaround is
  // clamping once the resulting viewport settles, via a one-shot `idle`.
  const clampListener = map.addListener("idle", () => {
    clampListener.remove()
    const zoom = map.getZoom()
    if (zoom !== undefined && zoom > MAX_FIT_ZOOM) map.setZoom(MAX_FIT_ZOOM)
  })
}

/**
 * Wraps the real Google Maps JS SDK: loads it, mounts a `google.maps.Map`
 * into a host `<div>`, keeps it in sync with `fitTo`/`data`/`selectedId`, and
 * reports viewport moves back up (debounced) and load failures. Renders
 * nothing on a provider failure — the parent (Task 20's page) shows the
 * failure state in its place.
 */
function MapCanvas({
  data,
  fitTo,
  selectedId,
  reducedMotion,
  onSelectFeature,
  onClusterClick,
  onViewportChange,
  onError,
  onLocateMe,
  locateMeLabel,
  fullscreenLabel,
  changeMapStyleLabel,
  mapTypeLabel,
  satelliteTypeLabel,
  nearCenter,
  nearRadiusMi
}: MapCanvasProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<google.maps.Map | null>(null)
  const [mapReady, setMapReady] = useState(false)
  const [providerFailed, setProviderFailed] = useState(false)

  // Task 20 — the hover tooltip's own state. Kept local here rather than in
  // Redux, following `mapReady`/`providerFailed` above rather than
  // `selectedId` (owned by `GlobalMapSlice`, threaded down as a prop):
  // unlike a selection, a hover never needs to survive a re-render of the
  // parent, drive a sibling panel, or round-trip through a `?feature=` deep
  // link — only this component ever reads it. `rect` is the hovered
  // marker's own `content` element's `getBoundingClientRect()`, taken by
  // `markers.ts` at the moment the mouse entered it.
  const [hoveredFeature, setHoveredFeature] = useState<{
    id: string
    rect: DOMRect
  } | null>(null)

  const handleHoverFeature = useCallback(
    (id: string | null, rect: DOMRect | null) => {
      setHoveredFeature(id && rect ? { id, rect } : null)
    },
    []
  )

  // Read through refs inside the mount effect below so it can stay a
  // run-once effect (it loads the SDK and constructs the map) without
  // re-running every time a parent passes a fresh inline callback.
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError
  const onViewportChangeRef = useRef(onViewportChange)
  onViewportChangeRef.current = onViewportChange
  const onLocateMeRef = useRef(onLocateMe)
  onLocateMeRef.current = onLocateMe
  const locateMeLabelRef = useRef(locateMeLabel)
  locateMeLabelRef.current = locateMeLabel
  const fullscreenLabelRef = useRef(fullscreenLabel)
  fullscreenLabelRef.current = fullscreenLabel
  const changeMapStyleLabelRef = useRef(changeMapStyleLabel)
  changeMapStyleLabelRef.current = changeMapStyleLabel
  const mapTypeLabelRef = useRef(mapTypeLabel)
  mapTypeLabelRef.current = mapTypeLabel
  const satelliteTypeLabelRef = useRef(satelliteTypeLabel)
  satelliteTypeLabelRef.current = satelliteTypeLabel

  useEffect(() => {
    let cancelled = false
    let idleListener: google.maps.MapsEventListener | undefined
    let debounceHandle: ReturnType<typeof setTimeout> | undefined
    // Set inside the mount callback below, once the Fullscreen/Map-type
    // controls' own `document`-level listeners (fullscreen state sync,
    // map-type menu outside-click/Escape) are registered — cleaned up here
    // so a `MapCanvas` unmount (tab switch away from the map) can't leak
    // handlers that keep firing against a detached DOM.
    let unsubscribeControlListeners: (() => void) | undefined

    // Google can resolve the SDK bootstrap successfully and only detect an
    // invalid/unauthorized key later, asynchronously — this catches that
    // case too, not just a rejected `loadGoogleMaps()` promise below.
    const unsubscribeAuthFailure = onMapsAuthFailure(() => {
      if (cancelled) return
      onErrorRef.current("map_provider_unavailable")
      setProviderFailed(true)
    })

    loadGoogleMaps()
      .then((maps) => {
        if (cancelled || !containerRef.current) return

        const map = new maps.Map(containerRef.current, {
          center: { lat: LA_FALLBACK.latitude, lng: LA_FALLBACK.longitude },
          zoom: LA_FALLBACK.zoom,
          mapId: import.meta.env.VITE_GOOGLE_MAPS_MAP_ID || DEMO_MAP_ID,
          // Zoom in/out (+/-) is on by default; `scaleControl` is not — the
          // reference's distance-scale legend needs it explicit. Zoom moves
          // to the top-left, under "Search this area" — matching the
          // reference's own left-side control cluster instead of Google's
          // default bottom-right. Street View (the "pegman") is switched
          // off: it added a floating icon with no place in the reference and
          // no product need here.
          zoomControl: true,
          zoomControlOptions: { position: maps.ControlPosition.LEFT_TOP },
          // `google.maps.ScaleControlOptions` has no `position` property —
          // its corner isn't configurable, unlike zoom's — so this stays at
          // Google's own fixed placement (bottom-right in this SDK version)
          // rather than the reference's bottom-left; a real, disclosed, and
          // otherwise-unfixable minor difference from the reference image.
          scaleControl: true,
          streetViewControl: false,
          // Both native (Map/Satellite, fullscreen) — see the custom-built
          // replacements below, pushed onto the same `LEFT_TOP` array as
          // zoom. Google gives no way to control a native control's ORDER
          // relative to another native control sharing one position (its
          // own internal creation sequence decides that, observed to put
          // fullscreen above zoom despite this options object declaring
          // zoom first) — the only way to guarantee "Map immediately right
          // of Fullscreen, Focus immediately right of Map" (explicit product
          // direction) is to own the DOM for all three and lay them out in
          // one flex row ourselves.
          mapTypeControl: false,
          fullscreenControl: false
        })
        mapRef.current = map

        // The Fullscreen / Map-Satellite / Focus row — all three fully
        // custom DOM (Google's control API takes a raw `Node`, not a React
        // tree, matching `markers.ts`'s own established plain-DOM
        // convention), wrapped in one flex container so THIS component
        // controls their left-to-right order and spacing deterministically,
        // rather than depending on Google's native per-position stacking
        // (see the constructor options above for why that isn't reliable).
        // Pushed as one entry onto the same `LEFT_TOP` array the native zoom
        // control also uses, so Google's own "reserve space per edge so
        // controls at one position never overlap another" layout keeps this
        // whole row directly below zoom — this is the one guarantee this
        // component DOES get from Google's layout for free.
        const controlsRow = document.createElement("div")
        controlsRow.className = "global-map-top-controls-row"

        const fullscreenButton = document.createElement("button")
        fullscreenButton.type = "button"
        fullscreenButton.setAttribute("aria-label", fullscreenLabelRef.current)
        fullscreenButton.setAttribute("aria-pressed", "false")
        fullscreenButton.className = "global-map-control-button"

        const EXPAND_ICON =
          '<svg viewBox="0 0 16 16" width="10" height="10" fill="none" stroke="currentColor" ' +
          'stroke-width="1.6" stroke-linecap="round" aria-hidden="true">' +
          '<path d="M1 5.5V1h4.5M10.5 1H15v4.5M15 10.5V15h-4.5M5.5 15H1v-4.5"/></svg>'
        const COMPRESS_ICON =
          '<svg viewBox="0 0 16 16" width="10" height="10" fill="none" stroke="currentColor" ' +
          'stroke-width="1.6" stroke-linecap="round" aria-hidden="true">' +
          '<path d="M5.5 1v4.5H1M15 5.5h-4.5V1M10.5 15v-4.5H15M1 10.5h4.5V15"/></svg>'
        fullscreenButton.innerHTML = EXPAND_ICON

        const handleFullscreenChange = () => {
          const isFullscreen =
            document.fullscreenElement === containerRef.current
          fullscreenButton.setAttribute("aria-pressed", String(isFullscreen))
          fullscreenButton.innerHTML = isFullscreen
            ? COMPRESS_ICON
            : EXPAND_ICON
        }
        document.addEventListener("fullscreenchange", handleFullscreenChange)
        fullscreenButton.addEventListener("click", () => {
          if (document.fullscreenElement === containerRef.current) {
            void document.exitFullscreen?.()
          } else {
            void containerRef.current?.requestFullscreen?.()
          }
        })

        // Map/Satellite — a minimal custom replacement for Google's native
        // `mapTypeControl`'s `DROPDOWN_MENU` style: a button that opens a
        // 2-option popup (this map has no vector styling to offer a Hybrid/
        // Terrain choice worth adding — see the `DEMO_MAP_ID` comment
        // above). The label always reads "Map" regardless of the active
        // type, matching the reference mockup and Google's own native
        // button (which also never swaps its label to "Satellite").
        const mapTypeWrapper = document.createElement("div")
        mapTypeWrapper.className = "global-map-maptype-control"

        const mapTypeButton = document.createElement("button")
        mapTypeButton.type = "button"
        mapTypeButton.setAttribute("aria-label", changeMapStyleLabelRef.current)
        mapTypeButton.setAttribute("aria-haspopup", "true")
        mapTypeButton.setAttribute("aria-expanded", "false")
        mapTypeButton.className =
          "global-map-control-button global-map-maptype-button"
        mapTypeButton.innerHTML =
          `<span>${mapTypeLabelRef.current}</span>` +
          '<svg viewBox="0 0 10 6" width="8" height="5" fill="currentColor" aria-hidden="true">' +
          '<path d="M0 0l5 6 5-6z"/></svg>'

        const mapTypeMenu = document.createElement("div")
        mapTypeMenu.className = "global-map-maptype-menu"
        mapTypeMenu.hidden = true

        const closeMapTypeMenu = () => {
          mapTypeMenu.hidden = true
          mapTypeButton.setAttribute("aria-expanded", "false")
        }

        const makeMapTypeOption = (label: string, mapTypeId: string) => {
          const option = document.createElement("button")
          option.type = "button"
          option.className = "global-map-maptype-option"
          option.textContent = label
          option.addEventListener("click", () => {
            map.setMapTypeId(mapTypeId)
            closeMapTypeMenu()
          })
          return option
        }
        mapTypeMenu.appendChild(
          makeMapTypeOption(mapTypeLabelRef.current, maps.MapTypeId.ROADMAP)
        )
        mapTypeMenu.appendChild(
          makeMapTypeOption(
            satelliteTypeLabelRef.current,
            maps.MapTypeId.SATELLITE
          )
        )

        mapTypeButton.addEventListener("click", (event) => {
          event.stopPropagation()
          if (mapTypeMenu.hidden) {
            mapTypeMenu.hidden = false
            mapTypeButton.setAttribute("aria-expanded", "true")
          } else {
            closeMapTypeMenu()
          }
        })
        const handleDocumentClickForMapType = (event: MouseEvent) => {
          if (!mapTypeWrapper.contains(event.target as Node)) closeMapTypeMenu()
        }
        const handleDocumentKeydownForMapType = (event: KeyboardEvent) => {
          if (event.key === "Escape") closeMapTypeMenu()
        }
        document.addEventListener("click", handleDocumentClickForMapType)
        document.addEventListener("keydown", handleDocumentKeydownForMapType)

        mapTypeWrapper.appendChild(mapTypeButton)
        mapTypeWrapper.appendChild(mapTypeMenu)

        // "Focus" (recenter on the browser's own geolocated position).
        const locateButton = document.createElement("button")
        locateButton.type = "button"
        locateButton.setAttribute("aria-label", locateMeLabelRef.current)
        locateButton.className = "global-map-control-button"
        locateButton.innerHTML =
          '<svg viewBox="0 0 14 14" width="10" height="10" fill="currentColor" aria-hidden="true">' +
          '<path d="M7 0a.75.75 0 01.75.75v1.3A5.002 5.002 0 0111.95 6.25h1.3a.75.75 0 010 1.5h-1.3' +
          "A5.002 5.002 0 017.75 11.95v1.3a.75.75 0 01-1.5 0v-1.3A5.002 5.002 0 012.05 7.75H.75a." +
          "75.75 0 010-1.5h1.3A5.002 5.002 0 016.25 2.05V.75A.75.75 0 017 0zm0 3.5A3.5 3.5 0 107 " +
          '10.5 3.5 3.5 0 007 3.5zm0 1.5a2 2 0 11-2 2 2 2 0 012-2z"/></svg>'
        locateButton.addEventListener("click", () => onLocateMeRef.current?.())

        controlsRow.appendChild(fullscreenButton)
        controlsRow.appendChild(mapTypeWrapper)
        controlsRow.appendChild(locateButton)
        // `TOP_LEFT`, not `LEFT_TOP` (zoom's own position) — an
        // `insertAt(0, ...)` on the SAME array as zoom was tried first and
        // didn't work: Google's real `MVCArray`/layout pass doesn't honor
        // that index the way the mocked test fixture's plain-array shim
        // does (confirmed live: the row still rendered below zoom despite
        // index 0). `TOP_LEFT` and `LEFT_TOP` are two separate,
        // Google-managed regions on the same edge — Google's own layout
        // reserves the top strip for `TOP_LEFT`/`TOP_CENTER`/`TOP_RIGHT`
        // content first and starts `LEFT_TOP` below whatever that strip
        // used, so this row is GUARANTEED above zoom regardless of any
        // array-ordering quirk, per explicit product direction (this row
        // above, zoom pushed down).
        map.controls[maps.ControlPosition.TOP_LEFT].push(controlsRow)

        unsubscribeControlListeners = () => {
          document.removeEventListener(
            "fullscreenchange",
            handleFullscreenChange
          )
          document.removeEventListener("click", handleDocumentClickForMapType)
          document.removeEventListener(
            "keydown",
            handleDocumentKeydownForMapType
          )
        }

        idleListener = map.addListener("idle", () => {
          if (debounceHandle) clearTimeout(debounceHandle)
          debounceHandle = setTimeout(() => {
            const viewport = readViewport(map)
            if (viewport) onViewportChangeRef.current(viewport)
          }, VIEWPORT_DEBOUNCE_MS)
        })

        setMapReady(true)
      })
      .catch(() => {
        if (cancelled) return
        onErrorRef.current("map_provider_unavailable")
        setProviderFailed(true)
      })

    return () => {
      cancelled = true
      unsubscribeAuthFailure()
      unsubscribeControlListeners?.()
      if (debounceHandle) clearTimeout(debounceHandle)
      idleListener?.remove()
      mapRef.current = null
    }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !fitTo) return
    applyFit(map, fitTo, reducedMotion)
  }, [fitTo, reducedMotion, mapReady])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !data) return

    const dispose = renderOverlays(map, {
      data,
      selectedId,
      onSelectFeature,
      onClusterClick,
      onHoverFeature: handleHoverFeature
    })

    return () => {
      dispose()
      // Every marker is torn down and rebuilt whenever this effect re-runs
      // (a data refresh, or a click that changes `selectedId`) — the DOM
      // element the mouse is still physically over gets destroyed without
      // ever getting to fire its own `mouseleave`. Clearing here means a
      // stale tooltip can never survive that rebuild: on a fresh data set it
      // simply disappears (guarding the "stale hover" case), and on a click
      // it disappears the instant the newly-selected marker's overlay is
      // rebuilt — superseded by the quick-view panel opening, not left to
      // flicker over it.
      setHoveredFeature(null)
    }
  }, [
    data,
    selectedId,
    onSelectFeature,
    onClusterClick,
    handleHoverFeature,
    mapReady
  ])

  // "Look Around a Zip Code" — the center pin + radius circle. A
  // `google.maps.Circle` is a plain overlay (no Map ID requirement, unlike
  // `AdvancedMarkerElement` — see the `DEMO_MAP_ID` comment above), added and
  // removed directly rather than through `markers.ts`'s `renderOverlays`,
  // which owns the feature/cluster marker set specifically. `clickable:
  // false` on the circle keeps it from intercepting clicks meant for a
  // marker underneath — it's a reference area, not something to click.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !nearCenter || nearRadiusMi == null) return

    const center = { lat: nearCenter.latitude, lng: nearCenter.longitude }

    const circle = new google.maps.Circle({
      map,
      center,
      radius: nearRadiusMi * METERS_PER_MILE,
      clickable: false,
      fillColor: NEAR_CENTER_COLOR,
      fillOpacity: 0.08,
      strokeColor: NEAR_CENTER_COLOR,
      strokeOpacity: 0.5,
      strokeWeight: 1.5,
      zIndex: 0
    })

    const pin = document.createElement("div")
    pin.className = "global-map-near-center-pin"
    pin.setAttribute("aria-hidden", "true")

    const pinMarker = new google.maps.marker.AdvancedMarkerElement({
      map,
      position: center,
      content: pin,
      // Non-interactive — a reference point, not a selectable record.
      gmpClickable: false,
      zIndex: 1
    })

    return () => {
      circle.setMap(null)
      pinMarker.map = null
    }
  }, [nearCenter?.latitude, nearCenter?.longitude, nearRadiusMi, mapReady])

  // Guards against a stale hover surviving a data refresh that still
  // manages to land inside the same effect pass (e.g. `data` changing
  // identity without `selectedId` changing) — the effect above already
  // clears `hoveredFeature` on every teardown, but this is the render-time
  // half of the same guard: never trust a hovered id the CURRENT `data`
  // doesn't actually contain.
  const hoveredMapFeature: MapFeature | null = useMemo(() => {
    if (!hoveredFeature || !data || data.mode !== "features") return null
    return (
      data.features.find((feature) => feature.id === hoveredFeature.id) ?? null
    )
  }, [hoveredFeature, data])

  if (providerFailed) return null

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <div
        ref={containerRef}
        data-testid="global-map-canvas"
        style={{ width: "100%", height: "100%" }}
      />
      {hoveredFeature && hoveredMapFeature && (
        <div
          style={tooltipPositionStyle(
            hoveredFeature.rect,
            containerRef.current
          )}
          className="pointer-events-none z-20"
        >
          <MarkerHoverTooltip feature={hoveredMapFeature} />
        </div>
      )}
    </div>
  )
}

export default MapCanvas
export { tooltipPositionStyle }
export type { MapCanvasProps }
